import { useState, useEffect, useMemo, useCallback } from 'react'
import { LogStats } from '../useLogStats'
import { FailGroup, FailGroupItem } from '../api'
import {
  Download, Zap, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown,
  Trophy, AlertCircle, Hourglass, Globe, Clock, Activity,
  ShieldCheck, BarChart3, Inbox, SkipForward,
} from 'lucide-react'

interface Props {
  stats: LogStats
  elapsedSec?: number
  running?: boolean
  campaignId?: string | null
  mode?: 'booster' | 'profiler'
}

interface ParsedRow {
  site: string
  key: string
  position: string
  elapsed: string
  url?: string
  brand?: string
  campaign?: string
  count: number
  isSuccess: boolean
}

interface GroupItem {
  site: string
  key: string
  proxy: string
  captcha: string
  worker: string
  ts: number
}

interface ReasonGroup {
  reason: string
  count: number
  items: GroupItem[]
}

const REASON_LABELS: Record<string, string> = {
  proxy_all_failed: 'Ошибка при работе с прокси (connect)',
  serp_load_failed: 'Выдача не загрузилась',
  'SERP не загрузился': 'SERP не загрузился',
  'сайт не найден в выдаче': 'Сайт не найден',
  not_found: 'Сайт не найден',
  captcha_unsolved: 'Капча не решена',
  browser_unresponsive: 'Браузер не отвечает',
  browser_crash: 'Браузер упал',
  browser_connect_fail: 'Не удалось подключиться к браузеру',
  browser_timeout: 'Таймаут браузера',
  click_did_not_navigate: 'Клик не привёл к переходу',
  yandex_429: 'Яндекс 429 (блокировка IP)',
  'Яндекс 429 (IP временно заблокирован)': 'Яндекс 429 (блокировка IP)',
  error: 'Общая ошибка',
}

const HIDE_SITES_IN_HEADER = new Set(['Яндекс 429 (IP временно заблокирован)'])

function getReasonLabel(reason: string): string {
  if (REASON_LABELS[reason]) return REASON_LABELS[reason]
  if (reason.startsWith('429 Too Many Requests')) return `Яндекс 429 — ${reason.replace('429 Too Many Requests — ', '')}`
  return reason
}

function shouldHideSiteInHeader(reason: string): boolean {
  if (HIDE_SITES_IN_HEADER.has(reason)) return true
  if (reason.startsWith('429 Too Many Requests')) return true
  return false
}

function sumMap(m: Map<string, number>): number {
  let s = 0
  for (const v of m.values()) s += v
  return s
}

function mapEntries(m: Map<string, number>): [string, number][] {
  return [...m.entries()].sort((a, b) => b[1] - a[1])
}

function parseEntries(entries: [string, number][], isSuccess: boolean): ParsedRow[] {
  return entries.map(([raw, count]) => {
    let campaign: string | undefined
    let label = raw
    const tabIdx = raw.indexOf('\t')
    if (tabIdx !== -1) {
      campaign = raw.substring(0, tabIdx)
      label = raw.substring(tabIdx + 1)
    }

    const parts = label.split(' — ')

    if (isSuccess && parts.length >= 3) {
      const site = parts[0]?.trim() || '?'
      const last = parts[parts.length - 1]?.trim() || ''
      let key = parts.slice(1, -1).join(' — ')
      let brand: string | undefined

      const brandMatch = key.match(/\s*\(\s*\+\s*(.+?)\s*\)\s*$/)
      if (brandMatch) {
        brand = brandMatch[1]
        key = key.substring(0, brandMatch.index).trim()
      }

      const subParts = last.split('|').map(s => s.trim())
      const position = subParts[0] || ''
      const elapsed = subParts[1] || ''
      const url = subParts[2] || undefined

      return { site, key, position, elapsed, url, brand, campaign, count, isSuccess }
    }

    const site = parts[0]?.trim() || '?'
    const key = parts.slice(1).join(' — ').trim() || '?'
    return { site, key, position: '—', elapsed: '', campaign, count, isSuccess }
  })
}

function formatTs(ts: number): string {
  return ts
    ? new Date(ts * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : ''
}

function useSpeed(total: number, firstTs: number): string {
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    if (!firstTs) return
    const id = setInterval(() => setNow(Date.now()), 10_000)
    return () => clearInterval(id)
  }, [firstTs])

  if (!firstTs || total === 0) return '—'
  const hours = (now - firstTs) / 3_600_000
  if (hours < 0.005) return '—'
  const speed = total / hours
  return speed >= 10 ? speed.toFixed(0) : speed.toFixed(1)
}

function downloadTsv(
  okGrouped: Record<string, OkGroup>,
  failGrouped: Record<string, FailGroup>,
) {
  // We pull straight from `okGrouped`/`failGrouped` because those are
  // persisted server-side in run_data.json and pushed via WS state.
  // The previous implementation built the TSV from `stats.boosterOk`
  // (a Map populated only by *live* websocket logs in this session) —
  // so users who opened the panel after the run had already started
  // got an empty file (bug #10 from /bugs).
  const TAB = '\t'
  const lines: string[] = []
  const esc = (s: string | undefined) => (s || '').replace(/\t/g, ' ').replace(/\n/g, ' ')

  // Successful runs — flatten OkGroup.items so the user gets one TSV
  // row per actual successful click rather than per-bucket aggregates.
  const okSites = Object.keys(okGrouped).sort()
  if (okSites.length > 0) {
    lines.push(['Статус', 'Сайт', 'Ключ', 'Бренд', 'Позиция', 'Время', 'URL', 'Капча'].join(TAB))
    for (const site of okSites) {
      const grp = okGrouped[site]
      if (!grp) continue
      const items = Array.isArray(grp.items) ? grp.items : []
      if (items.length === 0) {
        lines.push(['Успешные', esc(site), '', '', '', '', '', ''].join(TAB))
        continue
      }
      for (const it of items) {
        lines.push([
          'Успешные', esc(site), esc(it.key), esc(it.brand || ''),
          esc(it.position), esc(it.elapsed), esc(it.url),
          esc(it.captcha || ''),
        ].join(TAB))
      }
    }
  }

  // Failures — same treatment, one row per failed item.
  const failReasons = Object.keys(failGrouped).sort()
  if (failReasons.length > 0) {
    if (lines.length > 0) lines.push('')
    lines.push(['Статус', 'Причина', 'Сайт', 'Ключ', 'Прокси', 'Капча'].join(TAB))
    for (const reason of failReasons) {
      const grp = failGrouped[reason]
      if (!grp) continue
      const items = Array.isArray((grp as any).items) ? (grp as any).items as FailGroupItem[] : []
      if (items.length === 0) {
        lines.push(['Неудачные', esc(reason), '', '', '', ''].join(TAB))
        continue
      }
      for (const it of items) {
        lines.push([
          'Неудачные', esc(reason), esc(it.site), esc(it.key),
          esc(it.proxy), esc(it.captcha || ''),
        ].join(TAB))
      }
    }
  }

  if (lines.length === 0) {
    // Nothing to write: don't generate an empty file silently — alert
    // the user instead so the bug-#10 case is loud, not invisible.
    alert('Нет данных для экспорта')
    return
  }

  const BOM = '\uFEFF'
  const blob = new Blob([BOM + lines.join('\n')], { type: 'text/tab-separated-values;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const ts = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-')
  a.download = `report_${ts}.tsv`
  a.click()
  URL.revokeObjectURL(url)
}

// Полная выгрузка идёт с сервера (журнал всех результатов кампании): в WS-state
// items урезаны до 100 строк на группу, и локальная сборка файла из них давала
// «выгружает только часть» (bug #266). Локальный downloadTsv — только фолбэк
// без campaignId.
function downloadFromServer(campaignId: string, mode: 'booster' | 'profiler') {
  const a = document.createElement('a')
  a.href = `/api/run-stats/export?cid=${encodeURIComponent(campaignId)}&mode=${mode}`
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

interface FullLoadMeta { available: number; count: number; source: string; error?: boolean }

async function fetchGroupItems(campaignId: string | null | undefined, kind: string, group: string) {
  if (!campaignId) return null
  const r = await fetch(
    `/api/run-stats/items?cid=${encodeURIComponent(campaignId)}&kind=${kind}&group=${encodeURIComponent(group)}&limit=5000`,
  )
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  const d = await r.json()
  if (!Array.isArray(d?.items)) throw new Error('bad response')
  return d as { items: any[]; available: number; count: number; source: string }
}

function FullLoadFooter({ shown, count, meta, loading, onLoad }: {
  shown: number; count: number; meta?: FullLoadMeta; loading: boolean; onLoad: () => void
}) {
  const loaded = !!meta && !meta.error
  return (
    <div className="px-3 py-1.5 text-xs text-muted-foreground bg-muted/20 border-t border-border/30 flex items-center justify-between gap-2">
      <span>
        Показано {shown.toLocaleString('ru-RU')} из {count.toLocaleString('ru-RU')}
        {loaded && shown < count && (
          <span className="ml-1 text-muted-foreground/80">
            {meta!.source === 'memory'
              ? '— этот запуск начат до обновления панели, подробные строки сохранились частично'
              : meta!.available > shown
                ? '— последние записи; все строки — в файле «Скачать»'
                : '— остальные строки записаны до обновления панели и недоступны'}
          </span>
        )}
      </span>
      {!loaded && shown < count && (
        <button
          type="button"
          onClick={onLoad}
          disabled={loading}
          className="text-primary hover:underline disabled:opacity-50 ml-2 shrink-0"
        >
          {loading ? 'Загрузка...' : meta?.error ? 'Ошибка — повторить' : 'Загрузить все'}
        </button>
      )}
    </div>
  )
}

function downloadProfilerTsv(
  okGrouped: Record<string, { count: number; items: any[] }>,
  failGrouped: Record<string, FailGroup>,
) {
  const TAB = '\t'
  const lines: string[] = []
  const esc = (s: string | number | undefined) => String(s ?? '').replace(/\t/g, ' ').replace(/\n/g, ' ')

  const okGroups = Object.keys(okGrouped).sort()
  if (okGroups.length > 0) {
    lines.push(['Статус', 'Группа', 'Режим', 'Уровень', 'Время (с)', 'PID'].join(TAB))
    for (const group of okGroups) {
      const grp = okGrouped[group]
      if (!grp) continue
      const items = Array.isArray(grp.items) ? grp.items : []
      if (items.length === 0) {
        lines.push(['Прокачки', esc(group), '', '', '', ''].join(TAB))
        continue
      }
      for (const it of items) {
        lines.push([
          'Прокачки', esc(group), esc(it.mode), esc(it.level),
          esc(it.elapsed), esc(it.pid),
        ].join(TAB))
      }
    }
  }

  const failReasons = Object.keys(failGrouped).sort()
  if (failReasons.length > 0) {
    if (lines.length > 0) lines.push('')
    lines.push(['Статус', 'Причина', 'Кол-во'].join(TAB))
    for (const reason of failReasons) {
      const grp = failGrouped[reason]
      if (!grp) continue
      lines.push(['Неудачные', esc(reason), esc((grp as any).count || 0)].join(TAB))
    }
  }

  if (lines.length === 0) {
    alert('Нет данных для экспорта')
    return
  }

  const BOM = '\uFEFF'
  const blob = new Blob([BOM + lines.join('\n')], { type: 'text/tab-separated-values;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const ts = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-')
  a.download = `profiler_report_${ts}.tsv`
  a.click()
  URL.revokeObjectURL(url)
}

type SortField = 'count' | 'site' | 'key' | 'url' | 'position' | 'elapsed'
type SortDir = 'asc' | 'desc'

function SortableTable({ rows, colorClass }: { rows: ParsedRow[]; colorClass: string }) {
  const [sortField, setSortField] = useState<SortField>('count')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir(field === 'count' || field === 'position' ? 'desc' : 'asc')
    }
  }

  const sorted = useMemo(() => {
    const arr = [...rows]
    const mul = sortDir === 'asc' ? 1 : -1
    arr.sort((a, b) => {
      switch (sortField) {
        case 'count': return (a.count - b.count) * mul
        case 'site': return a.site.localeCompare(b.site) * mul
        case 'key': return a.key.localeCompare(b.key) * mul
        case 'position': {
          const pa = parseInt(a.position) || 999, pb = parseInt(b.position) || 999
          return (pa - pb) * mul
        }
        case 'elapsed': {
          const ea = parseInt(a.elapsed) || 0, eb = parseInt(b.elapsed) || 0
          return (ea - eb) * mul
        }
        case 'url': return (a.url || '').localeCompare(b.url || '') * mul
        default: return 0
      }
    })
    return arr
  }, [rows, sortField, sortDir])

  if (rows.length === 0) return null

  const TH = ({ field, label, className: cls }: { field: SortField; label: string; className?: string }) => (
    <th
      className={`py-1.5 px-2 font-medium text-left cursor-pointer hover:bg-muted/80 transition-colors select-none ${cls || ''}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field
          ? sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  )

  return (
    <div className="rounded-lg border border-border overflow-x-auto">
      <table className="w-full text-xs min-w-[640px]">
        <thead>
          <tr className="bg-muted/50 border-b border-border text-muted-foreground">
            <TH field="count" label="#" className="w-[40px] text-center" />
            <TH field="site" label="Сайт" />
            <TH field="key" label="Ключ" />
            <TH field="url" label="URL" />
            <TH field="position" label="Позиция" className="w-[80px]" />
            <TH field="elapsed" label="Время" className="w-[60px]" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => (
            <tr key={idx} className="border-b border-border/30 last:border-0 hover:bg-muted/30 transition-colors">
              <td className={`py-1 px-2 text-center tabular-nums font-bold ${colorClass}`}>{row.count}</td>
              <td className="py-1 px-2 font-medium text-foreground max-w-[140px] truncate" title={row.site}>
                {row.campaign && <span className="text-muted-foreground font-normal mr-1">[{row.campaign}]</span>}
                {row.site}
              </td>
              <td className="py-1 px-2 text-muted-foreground max-w-[200px] truncate" title={row.key}>{row.key}</td>
              <td className="py-1 px-2 text-muted-foreground max-w-[300px] truncate" title={row.url || '—'}>
                {row.url
                  ? <a href={row.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline">{row.url}</a>
                  : '—'}
              </td>
              <td className="py-1 px-2 tabular-nums">
                {row.brand
                  ? <span className="inline-flex items-center gap-1"><span className="text-blue-500 font-semibold" title={`Найден с брендом "${row.brand}"`}>бренд</span></span>
                  : row.position !== '—'
                    ? <span className={`font-semibold ${parseInt(row.position) <= 3 ? 'text-emerald-500' : parseInt(row.position) <= 10 ? 'text-chart-4' : 'text-amber-500'}`}>{row.position}</span>
                    : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="py-1 px-2 tabular-nums text-muted-foreground">{row.elapsed || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface OkGroupItem { key: string; url: string; position: string; brand?: string; captcha?: string; elapsed: string }
interface OkGroup { count: number; items: OkGroupItem[] }

function posColor(pos: string): string {
  const n = parseInt(pos)
  if (isNaN(n)) return 'text-muted-foreground'
  return n <= 3 ? 'text-emerald-500' : n <= 10 ? 'text-chart-4' : 'text-amber-500'
}

function shortUrl(url: string): string {
  try { return new URL(url).pathname.replace(/\/$/, '').slice(0, 50) || '/' } catch { return url.slice(0, 50) }
}

interface KeyAggregate {
  key: string
  count: number
  avgPos: number
  avgElapsed: number
  captchaCount: number
  urls: string[]
  items: OkGroupItem[]
}

function aggregateByKey(items: OkGroupItem[]): KeyAggregate[] {
  const map = new Map<string, KeyAggregate>()
  for (const item of items) {
    const k = item.key || '—'
    let agg = map.get(k)
    if (!agg) {
      agg = { key: k, count: 0, avgPos: 0, avgElapsed: 0, captchaCount: 0, urls: [], items: [] }
      map.set(k, agg)
    }
    agg.count++
    agg.items.push(item)
    const pos = parseInt(item.position)
    if (!isNaN(pos)) agg.avgPos += pos
    const el = parseInt(item.elapsed)
    if (!isNaN(el)) agg.avgElapsed += el
    if (item.captcha) agg.captchaCount++
    if (item.url && !agg.urls.includes(item.url)) agg.urls.push(item.url)
  }
  for (const agg of map.values()) {
    const posItems = agg.items.filter(i => !isNaN(parseInt(i.position)))
    const elItems = agg.items.filter(i => !isNaN(parseInt(i.elapsed)))
    agg.avgPos = posItems.length > 0 ? Math.round((agg.avgPos / posItems.length) * 10) / 10 : 0
    agg.avgElapsed = elItems.length > 0 ? Math.round(agg.avgElapsed / elItems.length) : 0
  }
  return [...map.values()].sort((a, b) => b.count - a.count)
}

function OkGroupedRows({ grouped, campaignId }: { grouped: Record<string, OkGroup>; campaignId?: string | null }) {
  const [openSites, setOpenSites] = useState<Set<string>>(new Set())
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set())
  // fullItems: загруженные полные данные по сайту (>100 items)
  const [fullItems, setFullItems] = useState<Record<string, any[]>>({})
  const [fullMeta, setFullMeta] = useState<Record<string, FullLoadMeta>>({})
  const [loadingFull, setLoadingFull] = useState<Set<string>>(new Set())

  const loadAll = useCallback(async (site: string) => {
    setLoadingFull(prev => { const n = new Set(prev); n.add(site); return n })
    try {
      const d = await fetchGroupItems(campaignId, 'ok', site)
      if (d) {
        setFullItems(prev => ({ ...prev, [site]: d.items }))
        setFullMeta(prev => ({ ...prev, [site]: { available: d.available, count: d.count, source: d.source } }))
      }
    } catch {
      setFullMeta(prev => ({ ...prev, [site]: { available: 0, count: 0, source: '', error: true } }))
    }
    setLoadingFull(prev => { const n = new Set(prev); n.delete(site); return n })
  }, [campaignId])

  const groups = useMemo(() =>
    Object.entries(grouped)
      .map(([site, data]) => ({ site, ...data }))
      .sort((a, b) => b.count - a.count),
  [grouped])

  const grandTotal = useMemo(() => groups.reduce((s, g) => s + g.count, 0), [groups])

  const toggleSite = useCallback((site: string) => {
    setOpenSites(prev => { const n = new Set(prev); if (n.has(site)) n.delete(site); else n.add(site); return n })
  }, [])

  const toggleKey = useCallback((id: string) => {
    setOpenKeys(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  }, [])

  if (groups.length === 0) return null

  return (
    <div className="space-y-1.5">
      {groups.map(({ site, count, items }) => {
        const expanded = openSites.has(site)
        const pct = grandTotal > 0 ? Math.round(count / grandTotal * 100) : 0
        const displayItems = fullItems[site] || (Array.isArray(items) ? items : [])
        const keyAggs = expanded ? aggregateByKey(displayItems) : []
        const isLoadingFull = loadingFull.has(site)
        return (
          <div key={site} className="rounded-lg border border-border overflow-hidden bg-card">
            <button type="button" onClick={() => toggleSite(site)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 sm:px-3 hover:bg-muted/40 transition-colors cursor-pointer select-none">
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              <Globe className="w-3.5 h-3.5 shrink-0 text-emerald-500/80" />
              <div className="flex flex-col items-start min-w-0 flex-1">
                <span className="text-sm font-semibold text-foreground truncate max-w-full">{site}</span>
                <span className="text-[10px] text-muted-foreground tabular-nums sm:hidden">
                  <span className="text-emerald-500 font-bold">{count.toLocaleString('ru-RU')}</span> · {pct}%
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-2 ml-auto shrink-0">
                <span className="tabular-nums text-sm font-bold text-emerald-500">{count.toLocaleString('ru-RU')}</span>
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-emerald-500/70 to-emerald-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">{pct}%</span>
              </div>
            </button>
            {expanded && keyAggs.length > 0 && (
              <div className="border-t border-border">
                {/* Desktop: full table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                        <th className="py-1.5 px-2 font-medium text-left w-5"></th>
                        <th className="py-1.5 px-2 font-medium text-left">Ключ</th>
                        <th className="py-1.5 px-2 font-medium text-center w-[50px]">Кол-во</th>
                        <th className="py-1.5 px-2 font-medium text-left">URL</th>
                        <th className="py-1.5 px-2 font-medium text-left w-[70px]">Позиция</th>
                        <th className="py-1.5 px-2 font-medium text-left w-[55px]">Капча</th>
                        <th className="py-1.5 px-2 font-medium text-left w-[55px]">Время</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keyAggs.map((agg) => {
                        const keyId = `${site}::${agg.key}`
                        const keyExpanded = openKeys.has(keyId)
                        const isMulti = agg.count > 1
                        return (
                          <OkKeyRow key={keyId} agg={agg} keyId={keyId} isMulti={isMulti}
                            expanded={keyExpanded} onToggle={toggleKey} />
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Mobile: card list */}
                <div className="md:hidden divide-y divide-border/40">
                  {keyAggs.map((agg) => {
                    const keyId = `${site}::${agg.key}`
                    const keyExpanded = openKeys.has(keyId)
                    return (
                      <OkKeyCard key={keyId} agg={agg} keyId={keyId}
                        expanded={keyExpanded} onToggle={toggleKey} />
                    )
                  })}
                </div>
                {count > (Array.isArray(items) ? items.length : 0) && (
                  <FullLoadFooter shown={displayItems.length} count={count} meta={fullMeta[site]}
                    loading={isLoadingFull} onLoad={() => loadAll(site)} />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function OkKeyCard({ agg, keyId, expanded, onToggle }: {
  agg: KeyAggregate; keyId: string; expanded: boolean; onToggle: (id: string) => void
}) {
    const isMulti = agg.count > 1
  const first = Array.isArray(agg.items) && agg.items.length > 0 ? agg.items[0] : null
  return (
    <div className="px-2.5 py-2">
      <button
        type="button"
        onClick={() => isMulti && onToggle(keyId)}
        className={`w-full flex items-start gap-2 text-left ${isMulti ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {isMulti && (
          <ChevronRight className={`w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[13px] font-medium text-foreground break-words leading-snug">{agg.key}</span>
            {isMulti && (
              <span className="shrink-0 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-success/10 text-success border border-success/20 tabular-nums">
                {agg.count}×
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <span className="text-muted-foreground/70">поз:</span>
              <span className={`font-bold tabular-nums ${posColor(isMulti ? String(agg.avgPos) : first.position)}`}>
                {isMulti ? `~${agg.avgPos}` : (first.position || '—')}
              </span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="text-muted-foreground/70">время:</span>
              <span className="font-medium tabular-nums text-foreground/80">
                {isMulti ? `~${agg.avgElapsed}с` : (first.elapsed || '—')}
              </span>
            </span>
            {(isMulti ? agg.captchaCount > 0 : !!first.captcha) && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 tabular-nums">
                капча {isMulti ? `${agg.captchaCount}/${agg.count}` : 'да'}
              </span>
            )}
            {first.brand && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                +{first.brand}
              </span>
            )}
          </div>
          {(isMulti ? agg.urls.length === 1 && agg.urls[0] : first.url) && (
            <div className="mt-1 text-[11px] font-mono text-muted-foreground/80 truncate">
              {shortUrl(isMulti ? agg.urls[0] : first.url || '')}
            </div>
          )}
        </div>
      </button>
      {expanded && isMulti && (
        <div className="mt-2 ml-5 space-y-1.5">
          {(agg.items || []).map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 text-[11px] py-1 px-2 rounded bg-muted/30">
              <span className={`tabular-nums font-semibold ${posColor(item.position)}`}>{item.position || '—'}</span>
              <span className="text-muted-foreground/70 tabular-nums">{item.elapsed || '—'}</span>
              {item.url && <span className="text-muted-foreground/60 truncate font-mono">{shortUrl(item.url)}</span>}
              {item.captcha && <span className="ml-auto text-cyan-400">капча</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function OkKeyRow({ agg, keyId, isMulti, expanded, onToggle }: {
  agg: KeyAggregate; keyId: string; isMulti: boolean; expanded: boolean; onToggle: (id: string) => void
}) {
  const first = Array.isArray(agg.items) && agg.items.length > 0 ? agg.items[0] : null
  if (!isMulti) {
    return (
      <tr className="border-b border-border/20 last:border-0 hover:bg-muted/20 transition-colors">
        <td className="py-1 px-2"></td>
        <td className="py-1 px-2 text-foreground max-w-[200px] truncate" title={agg.key}>{agg.key}</td>
        <td className="py-1 px-2 text-center text-muted-foreground tabular-nums">1</td>
        <td className="py-1 px-2 text-muted-foreground max-w-[150px] truncate font-mono text-[11px]" title={first.url}>{first.url ? shortUrl(first.url) : '—'}</td>
        <td className="py-1 px-2 tabular-nums">
          <span className={`font-semibold ${posColor(first.position)}`}>{first.position || '—'}</span>
          {first.brand && <span className="text-blue-400 ml-1 text-[10px]">(+{first.brand})</span>}
        </td>
        <td className="py-1 px-2">
          {first.captcha
            ? <span className="inline-block px-1 py-0.5 rounded text-[10px] font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">да</span>
            : <span className="text-muted-foreground/50">—</span>}
        </td>
        <td className="py-1 px-2 text-muted-foreground tabular-nums">{first.elapsed || '—'}</td>
      </tr>
    )
  }

  return (
    <>
      <tr onClick={() => onToggle(keyId)}
        className="border-b border-border/20 hover:bg-muted/20 transition-colors cursor-pointer select-none">
        <td className="py-1 px-2">
          <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`} />
        </td>
        <td className="py-1 px-2 text-foreground max-w-[200px] truncate font-medium" title={agg.key}>{agg.key}</td>
        <td className="py-1 px-2 text-center">
          <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-success/10 text-success border border-success/20 tabular-nums">{agg.count}×</span>
        </td>
        <td className="py-1 px-2 text-muted-foreground max-w-[150px] truncate font-mono text-[11px]" title={agg.urls[0]}>
          {agg.urls.length === 1 ? shortUrl(agg.urls[0]) : `${agg.urls.length} URL`}
        </td>
        <td className="py-1 px-2 tabular-nums">
          <span className={`font-semibold ${posColor(String(agg.avgPos))}`}>~{agg.avgPos}</span>
        </td>
        <td className="py-1 px-2">
          {agg.captchaCount > 0
            ? <span className="inline-block px-1 py-0.5 rounded text-[10px] font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 tabular-nums">{agg.captchaCount}/{agg.count}</span>
            : <span className="text-muted-foreground/50">—</span>}
        </td>
        <td className="py-1 px-2 text-muted-foreground tabular-nums">~{agg.avgElapsed}с</td>
      </tr>
      {expanded && (agg.items || []).map((item, idx) => (
        <tr key={idx} className="border-b border-border/10 last:border-0 bg-muted/10">
          <td className="py-0.5 px-2"></td>
          <td className="py-0.5 px-2 pl-5 text-muted-foreground text-[11px] max-w-[200px] truncate" title={item.key}>{item.key}</td>
          <td className="py-0.5 px-2"></td>
          <td className="py-0.5 px-2 text-muted-foreground/70 max-w-[150px] truncate font-mono text-[11px]" title={item.url}>{item.url ? shortUrl(item.url) : '—'}</td>
          <td className="py-0.5 px-2 tabular-nums">
            <span className={`${posColor(item.position)}`}>{item.position || '—'}</span>
            {item.brand && <span className="text-blue-400 ml-1 text-[10px]">(+{item.brand})</span>}
          </td>
          <td className="py-0.5 px-2">
            {item.captcha
              ? <span className="text-[10px] text-cyan-400">да</span>
              : <span className="text-muted-foreground/30">—</span>}
          </td>
          <td className="py-0.5 px-2 text-muted-foreground/70 tabular-nums text-[11px]">{item.elapsed || '—'}</td>
        </tr>
      ))}
    </>
  )
}

function SiteGroupedTable({ sites }: { sites: { site: string; items: ParsedRow[]; total: number }[] }) {
  const [open, setOpen] = useState<Set<string>>(new Set())
  const grandTotal = useMemo(() => sites.reduce((s, g) => s + g.total, 0), [sites])

  const toggle = useCallback((site: string) => {
    setOpen(prev => {
      const next = new Set(prev)
      if (next.has(site)) next.delete(site); else next.add(site)
      return next
    })
  }, [])

  return (
    <div className="space-y-1.5">
      {sites.map(({ site, items, total }) => {
        const expanded = open.has(site)
        const pct = grandTotal > 0 ? Math.round(total / grandTotal * 100) : 0
        return (
          <div key={site} className="rounded-lg border border-border overflow-hidden bg-card">
            <button
              type="button"
              onClick={() => toggle(site)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 sm:px-3 hover:bg-muted/40 transition-colors cursor-pointer select-none"
            >
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              <Globe className="w-3.5 h-3.5 shrink-0 text-emerald-500/80" />
              <div className="flex flex-col items-start min-w-0 flex-1">
                <span className="text-sm font-semibold text-foreground truncate max-w-full">{site}</span>
                <span className="text-[10px] text-muted-foreground tabular-nums sm:hidden">
                  <span className="text-emerald-500 font-bold">{total.toLocaleString('ru-RU')}</span> · {pct}%
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-2 ml-auto shrink-0">
                <span className="tabular-nums text-sm font-bold text-emerald-500">{total.toLocaleString('ru-RU')}</span>
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-emerald-500/70 to-emerald-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">{pct}%</span>
              </div>
            </button>
            {expanded && (
              <div className="border-t border-border overflow-x-auto">
                <SortableTable rows={items} colorClass="text-success" />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function SuccessRows({ rows }: { rows: ParsedRow[] }) {
  const grouped = useMemo(() => {
    const map = new Map<string, ParsedRow[]>()
    for (const r of rows) {
      const arr = map.get(r.site) || []
      arr.push(r)
      map.set(r.site, arr)
    }
    return [...map.entries()]
      .map(([site, items]) => ({ site, items, total: items.reduce((s, i) => s + i.count, 0) }))
      .sort((a, b) => b.total - a.total)
  }, [rows])

  return grouped.length <= 1
    ? <SortableTable rows={rows} colorClass="text-success" />
    : <SiteGroupedTable sites={grouped} />
}

function GroupedRows({ grouped, colorClass = 'text-destructive', barClass = 'bg-destructive/70' }: {
  grouped: Record<string, FailGroup>; colorClass?: string; barClass?: string
}) {
  const [open, setOpen] = useState<Set<string>>(new Set())

  const groups = useMemo<ReasonGroup[]>(() =>
    Object.entries(grouped)
      .map(([reason, data]) => ({ reason, ...data }))
      .sort((a, b) => b.count - a.count),
  [grouped])

  const totalCount = useMemo(() => groups.reduce((s, g) => s + g.count, 0), [groups])

  const toggle = useCallback((reason: string) => {
    setOpen(prev => {
      const next = new Set(prev)
      if (next.has(reason)) next.delete(reason); else next.add(reason)
      return next
    })
  }, [])

  if (groups.length === 0) return null

  const accentBar = barClass.includes('amber') ? 'from-amber-500/70 to-amber-400'
    : barClass.includes('sky') ? 'from-sky-500/70 to-sky-400'
    : 'from-destructive/70 to-red-400'

  return (
    <div className="space-y-1.5">
      {groups.map(({ reason, count, items }) => {
        const expanded = open.has(reason)
        const pct = totalCount > 0 ? Math.round(count / totalCount * 100) : 0
        const isProxy = reason === 'proxy_all_failed'
        const hideSite = shouldHideSiteInHeader(reason) || isProxy
        const safeItems = Array.isArray(items) ? items : []
        const uniqueSites = !hideSite ? [...new Set(safeItems.map(i => i.site).filter(s => s && s !== 'infra'))] : []
        return (
          <div key={reason} className="rounded-lg border border-border overflow-hidden bg-card">
            <button
              type="button"
              onClick={() => toggle(reason)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 sm:px-3 hover:bg-muted/40 transition-colors cursor-pointer select-none"
            >
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              <div className="flex flex-col items-start min-w-0 flex-1">
                <span className="text-sm font-medium text-foreground leading-snug truncate max-w-full">{getReasonLabel(reason)}</span>
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5">
                  <span className="sm:hidden">
                    <span className={`font-bold tabular-nums ${colorClass}`}>{count.toLocaleString('ru-RU')}</span> · {pct}%
                  </span>
                  {uniqueSites.length > 0 && uniqueSites.length <= 3 && (
                    <span className="truncate">{uniqueSites.join(', ')}</span>
                  )}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2 ml-auto shrink-0">
                <span className={`tabular-nums text-sm font-bold ${colorClass}`}>{count.toLocaleString('ru-RU')}</span>
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full rounded-full bg-gradient-to-r ${accentBar}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">{pct}%</span>
              </div>
            </button>
            {expanded && safeItems.length > 0 && (() => {
              const deduped = new Map<string, { label: string; proxy: string; cnt: number }>()
              for (const item of safeItems) {
                let label = ''
                let proxy = ''
                if (isProxy) {
                  proxy = item.proxy || '—'
                  label = ''
                } else {
                  const parts: string[] = []
                  if (item.site && item.site !== 'infra') parts.push(item.site)
                  if (item.key && item.key !== reason) parts.push(item.key)
                  label = parts.join(' — ') || reason
                  proxy = item.proxy || ''
                }
                const dedupKey = `${label}|||${proxy}`
                const existing = deduped.get(dedupKey)
                if (existing) { existing.cnt++ } else { deduped.set(dedupKey, { label, proxy, cnt: 1 }) }
              }
              const lines = [...deduped.values()].sort((a, b) => b.cnt - a.cnt)

              return (
                <div className="border-t border-border px-2.5 py-2 sm:px-3 space-y-1">
                  {lines.map(({ label, proxy, cnt }, idx) => (
                    <div key={idx} className="text-[12px] leading-relaxed flex items-baseline gap-2 py-0.5 px-1.5 rounded hover:bg-muted/30 transition-colors break-words">
                      {cnt > 1 && (
                        <span className={`shrink-0 inline-flex items-center justify-center min-w-[24px] h-[18px] px-1 rounded text-[10px] font-bold tabular-nums bg-current/10 ${colorClass}`}>
                          {cnt}×
                        </span>
                      )}
                      {isProxy ? (
                        <span className="text-foreground/90 break-all">
                          Нет соединения для <span className="font-mono text-[11px] text-orange-400">{proxy}</span>
                        </span>
                      ) : proxy ? (
                        <span className="text-foreground/90 break-words">
                          {label} <span className="text-muted-foreground/50">·</span> <span className="font-mono text-[11px] text-orange-400 break-all">{proxy}</span>
                        </span>
                      ) : (
                        <span className="text-foreground/90 break-words">{label}</span>
                      )}
                    </div>
                  ))}
                  {count > safeItems.length && (
                    <div className="text-[11px] text-muted-foreground pt-1.5 border-t border-border/30 mt-1.5 italic">
                      и ещё {(count - safeItems.length).toLocaleString('ru-RU')}…
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        )
      })}
    </div>
  )
}

// ── Profiler «данные по прокачке» ────────────────────────────────────
// Mirrors the booster OkGroupedRows shape, but grouped by PROFILE GROUP
// (party) and aggregated by warm MODE, with profiler-meaningful columns
// (mode / count / avg time / level) instead of site/key/position/url.

interface ProfilerOkItem { level: string | number; elapsed: string | number; ts?: number; pid?: string; mode?: string }
interface ProfilerOkGroup { count: number; items: ProfilerOkItem[] }

interface ModeAggregate {
  mode: string
  count: number
  avgElapsed: number
  avgLevel: number
  maxLevel: number
}

function toNum(v: string | number | undefined): number {
  if (typeof v === 'number') return v
  const n = parseFloat(String(v ?? ''))
  return isNaN(n) ? NaN : n
}

function aggregateByMode(items: ProfilerOkItem[]): ModeAggregate[] {
  const map = new Map<string, { mode: string; count: number; sumEl: number; nEl: number; sumLv: number; nLv: number; maxLv: number }>()
  for (const it of items) {
    const m = (it.mode || '').trim() || '—'
    let agg = map.get(m)
    if (!agg) { agg = { mode: m, count: 0, sumEl: 0, nEl: 0, sumLv: 0, nLv: 0, maxLv: 0 }; map.set(m, agg) }
    agg.count++
    const el = toNum(it.elapsed)
    if (!isNaN(el)) { agg.sumEl += el; agg.nEl++ }
    const lv = toNum(it.level)
    if (!isNaN(lv)) { agg.sumLv += lv; agg.nLv++; if (lv > agg.maxLv) agg.maxLv = lv }
  }
  return [...map.values()]
    .map(a => ({
      mode: a.mode,
      count: a.count,
      avgElapsed: a.nEl > 0 ? Math.round(a.sumEl / a.nEl) : 0,
      avgLevel: a.nLv > 0 ? Math.round((a.sumLv / a.nLv) * 10) / 10 : 0,
      maxLevel: a.maxLv,
    }))
    .sort((a, b) => b.count - a.count)
}

function groupAvgElapsed(items: ProfilerOkItem[]): number {
  let s = 0, n = 0
  for (const it of items) { const el = toNum(it.elapsed); if (!isNaN(el)) { s += el; n++ } }
  return n > 0 ? Math.round(s / n) : 0
}

function ProfilerOkGroupedRows({ grouped, campaignId }: { grouped: Record<string, ProfilerOkGroup>; campaignId?: string | null }) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())
  const [fullItems, setFullItems] = useState<Record<string, ProfilerOkItem[]>>({})
  const [fullMeta, setFullMeta] = useState<Record<string, FullLoadMeta>>({})
  const [loadingFull, setLoadingFull] = useState<Set<string>>(new Set())

  const loadAll = useCallback(async (group: string) => {
    setLoadingFull(prev => { const n = new Set(prev); n.add(group); return n })
    try {
      const d = await fetchGroupItems(campaignId, 'profiler_ok', group)
      if (d) {
        setFullItems(prev => ({ ...prev, [group]: d.items }))
        setFullMeta(prev => ({ ...prev, [group]: { available: d.available, count: d.count, source: d.source } }))
      }
    } catch {
      setFullMeta(prev => ({ ...prev, [group]: { available: 0, count: 0, source: '', error: true } }))
    }
    setLoadingFull(prev => { const n = new Set(prev); n.delete(group); return n })
  }, [campaignId])

  const groups = useMemo(() =>
    Object.entries(grouped)
      .map(([group, data]) => ({ group, ...data }))
      .sort((a, b) => b.count - a.count),
  [grouped])

  const grandTotal = useMemo(() => groups.reduce((s, g) => s + g.count, 0), [groups])

  const toggle = useCallback((group: string) => {
    setOpenGroups(prev => { const n = new Set(prev); if (n.has(group)) n.delete(group); else n.add(group); return n })
  }, [])

  if (groups.length === 0) return null

  return (
    <div className="space-y-1.5">
      {groups.map(({ group, count, items }) => {
        const expanded = openGroups.has(group)
        const pct = grandTotal > 0 ? Math.round(count / grandTotal * 100) : 0
        const displayItems = fullItems[group] || (Array.isArray(items) ? items : [])
        const modeAggs = expanded ? aggregateByMode(displayItems) : []
        const avgEl = groupAvgElapsed(displayItems)
        const isLoadingFull = loadingFull.has(group)
        return (
          <div key={group} className="rounded-lg border border-border overflow-hidden bg-card">
            <button type="button" onClick={() => toggle(group)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 sm:px-3 hover:bg-muted/40 transition-colors cursor-pointer select-none">
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              <Globe className="w-3.5 h-3.5 shrink-0 text-emerald-500/80" />
              <div className="flex flex-col items-start min-w-0 flex-1">
                <span className="text-sm font-semibold text-foreground truncate max-w-full">{group}</span>
                <span className="text-[10px] text-muted-foreground tabular-nums sm:hidden">
                  <span className="text-emerald-500 font-bold">{count.toLocaleString('ru-RU')}</span> · {pct}%{avgEl > 0 ? ` · ~${avgEl}с` : ''}
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-2 ml-auto shrink-0">
                {avgEl > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums" title="Среднее время прокачки">
                    <Clock className="w-3 h-3" />~{avgEl}с
                  </span>
                )}
                <span className="tabular-nums text-sm font-bold text-emerald-500">{count.toLocaleString('ru-RU')}</span>
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-emerald-500/70 to-emerald-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">{pct}%</span>
              </div>
            </button>
            {expanded && modeAggs.length > 0 && (
              <div className="border-t border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                      <th className="py-1.5 px-2 font-medium text-left">Режим</th>
                      <th className="py-1.5 px-2 font-medium text-center w-[70px]">Кол-во</th>
                      <th className="py-1.5 px-2 font-medium text-left w-[110px]">Среднее время</th>
                      <th className="py-1.5 px-2 font-medium text-left w-[120px]">Уровень</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modeAggs.map((agg) => (
                      <tr key={agg.mode} className="border-b border-border/20 last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="py-1 px-2 text-foreground max-w-[220px] truncate" title={agg.mode}>{agg.mode}</td>
                        <td className="py-1 px-2 text-center">
                          <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-success/10 text-success border border-success/20 tabular-nums">{agg.count}×</span>
                        </td>
                        <td className="py-1 px-2 text-muted-foreground tabular-nums">~{agg.avgElapsed}с</td>
                        <td className="py-1 px-2 tabular-nums text-foreground/80">
                          {agg.avgLevel > 0
                            ? <span>ср. <span className="font-semibold text-emerald-500">{agg.avgLevel}</span>{agg.maxLevel > 0 ? <span className="text-muted-foreground/70"> · макс {agg.maxLevel}</span> : null}</span>
                            : <span className="text-muted-foreground/50">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {count > (Array.isArray(items) ? items.length : 0) && (
                  <FullLoadFooter shown={displayItems.length} count={count} meta={fullMeta[group]}
                    loading={isLoadingFull} onLoad={() => loadAll(group)} />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Profiler «Неудачные прокачки» (reason → шаг/действие + прокси) ───────
// A profiler-specific fail table. Unlike the shared booster GroupedRows (which
// builds rows from site/key), profiler fail items carry WHERE the iteration
// died — step index, cube type, node id (scenario_engine._fail_context_suffix)
// — and WHICH proxy was in use. We render reason groups whose expanded rows
// dedupe by (step+type+proxy) so «Прокси» and «Шаг/Действие» are first-class.
function ProfilerFailGroupedRows({ grouped }: { grouped: Record<string, FailGroup> }) {
  const [open, setOpen] = useState<Set<string>>(new Set())

  const groups = useMemo(() =>
    Object.entries(grouped)
      .map(([reason, data]) => ({ reason, count: (data as FailGroup).count || 0, items: (data as FailGroup).items || [] }))
      .sort((a, b) => b.count - a.count),
  [grouped])

  const totalCount = useMemo(() => groups.reduce((s, g) => s + g.count, 0), [groups])

  const toggle = useCallback((reason: string) => {
    setOpen(prev => { const n = new Set(prev); if (n.has(reason)) n.delete(reason); else n.add(reason); return n })
  }, [])

  if (groups.length === 0) return null

  return (
    <div className="space-y-1.5">
      {groups.map(({ reason, count, items }) => {
        const expanded = open.has(reason)
        const pct = totalCount > 0 ? Math.round(count / totalCount * 100) : 0
        const safeItems: FailGroupItem[] = Array.isArray(items) ? items : []
        // Dedupe by failing step/action + proxy so repeated identical failures
        // collapse into one line with an ×N counter.
        const deduped = new Map<string, { step: string; type: string; node: string; proxy: string; cnt: number }>()
        for (const it of safeItems) {
          const step = (it.step || '').trim()
          const type = (it.type || '').trim()
          const node = (it.node || '').trim()
          const proxy = (it.proxy || '').trim()
          const k = `${step}|${type}|${node}|${proxy}`
          const ex = deduped.get(k)
          if (ex) ex.cnt++
          else deduped.set(k, { step, type, node, proxy, cnt: 1 })
        }
        const lines = [...deduped.values()].sort((a, b) => b.cnt - a.cnt)
        return (
          <div key={reason} className="rounded-lg border border-border overflow-hidden bg-card">
            <button type="button" onClick={() => toggle(reason)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 sm:px-3 hover:bg-muted/40 transition-colors cursor-pointer select-none">
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              <AlertCircle className="w-3.5 h-3.5 shrink-0 text-destructive/70" />
              <div className="flex flex-col items-start min-w-0 flex-1">
                <span className="text-sm font-medium text-foreground leading-snug truncate max-w-full">{getReasonLabel(reason)}</span>
                <span className="text-[10px] text-muted-foreground tabular-nums sm:hidden">
                  <span className="font-bold text-destructive">{count.toLocaleString('ru-RU')}</span> · {pct}%
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-2 ml-auto shrink-0">
                <span className="tabular-nums text-sm font-bold text-destructive">{count.toLocaleString('ru-RU')}</span>
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-destructive/70 to-red-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">{pct}%</span>
              </div>
            </button>
            {expanded && (
              <div className="border-t border-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                      <th className="py-1.5 px-2 font-medium text-left w-[60px]">Кол-во</th>
                      <th className="py-1.5 px-2 font-medium text-left">Шаг / Действие</th>
                      <th className="py-1.5 px-2 font-medium text-left">Прокси</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(({ step, type, node, proxy, cnt }, idx) => {
                      const stepLabel = step
                        ? `Шаг ${step}${type ? ` · ${type}` : ''}`
                        : (type || '—')
                      return (
                        <tr key={idx} className="border-b border-border/20 last:border-0 hover:bg-muted/20 transition-colors">
                          <td className="py-1 px-2">
                            <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-destructive/10 text-destructive border border-destructive/20 tabular-nums">{cnt}×</span>
                          </td>
                          <td className="py-1 px-2 text-foreground/90 break-words" title={node ? `node ${node}` : undefined}>
                            {stepLabel}
                          </td>
                          <td className="py-1 px-2">
                            {proxy
                              ? <span className="font-mono text-[11px] text-orange-400 break-all">{proxy}</span>
                              : <span className="text-muted-foreground/50">—</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {count > safeItems.length && (
                  <div className="px-3 py-1.5 text-[11px] text-muted-foreground bg-muted/20 border-t border-border/30 italic">
                    и ещё {(count - safeItems.length).toLocaleString('ru-RU')}…
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function SectionBlock({ title, count, colorClass, defaultOpen = false, totalAll, accent = 'emerald', icon: Icon, children }: {
  title: string
  count: number
  colorClass: string
  defaultOpen?: boolean
  totalAll?: number
  accent?: 'emerald' | 'destructive' | 'amber'
  icon?: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const pct = totalAll && totalAll > 0 ? Math.round((count / totalAll) * 100) : null
  const accentBg = accent === 'destructive' ? 'bg-destructive' : accent === 'amber' ? 'bg-amber-500' : 'bg-emerald-500'
  const accentRing = accent === 'destructive' ? 'group-hover:ring-destructive/30' : accent === 'amber' ? 'group-hover:ring-amber-500/30' : 'group-hover:ring-emerald-500/30'
  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`group w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-border bg-card hover:bg-muted/40 hover:border-foreground/15 transition-all cursor-pointer select-none ring-0 hover:ring-1 ${accentRing}`}
      >
        <span className={`w-1 h-7 rounded-full ${accentBg} shrink-0`} />
        <ChevronRight className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        {Icon && <Icon className={`w-4 h-4 shrink-0 ${colorClass}`} />}
        <span className="text-sm font-semibold text-foreground truncate">{title}</span>
        <span className={`tabular-nums text-sm font-bold ${colorClass}`}>{count.toLocaleString('ru-RU')}</span>
        {pct !== null && (
          <span className="hidden sm:inline-flex items-center gap-1.5 ml-auto text-[11px] text-muted-foreground tabular-nums">
            <span className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
              <span className={`block h-full ${accentBg} opacity-70`} style={{ width: `${pct}%` }} />
            </span>
            <span className="w-8 text-right">{pct}%</span>
          </span>
        )}
        {pct !== null && (
          <span className="sm:hidden ml-auto text-[11px] text-muted-foreground tabular-nums">{pct}%</span>
        )}
      </button>
      {open && <div className="mt-2 pl-1">{children}</div>}
    </div>
  )
}

function NonBoosterSection({ title, count, colorClass, entries }: {
  title: string; count: number; colorClass: string; entries: [string, number][]
}) {
  if (count === 0 && entries.length === 0) return null
  return (
    <div className="mb-5">
      <div className="mb-1.5 pb-1 border-b border-border">
        <span className={`font-bold text-sm ${colorClass}`}>{title}({count})</span>
      </div>
      {entries.length > 0 && (
        <div className="ml-1">
          <div className="text-xs font-medium mb-1 text-muted-foreground">Сообщения:</div>
          {entries.map(([msg, cnt], i) => (
            <div key={i} className="flex gap-2 text-xs leading-relaxed py-[1px]">
              <span className={`shrink-0 tabular-nums text-right min-w-[28px] font-bold ${colorClass}`}>{cnt}</span>
              <span className="text-muted-foreground">–</span>
              <span className="break-all text-foreground">{msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function sumGrouped(g: Record<string, any>): number {
  let s = 0
  for (const v of Object.values(g)) s += (v?.count || 0)
  return s
}

type StatColor = 'emerald' | 'destructive' | 'amber' | 'yellow' | 'violet' | 'sky'
const STAT_COLORS: Record<StatColor, {
  dot: string; text: string; bg: string; border: string; icon: string; ring: string;
}> = {
  'emerald':     { dot: 'bg-emerald-500', text: 'text-emerald-500', bg: 'bg-emerald-500/[0.08]',     border: 'border-emerald-500/20',     icon: 'text-emerald-500',     ring: 'ring-emerald-500/30' },
  'destructive': { dot: 'bg-destructive', text: 'text-destructive', bg: 'bg-destructive/[0.08]',     border: 'border-destructive/20',     icon: 'text-destructive',     ring: 'ring-destructive/30' },
  'amber':       { dot: 'bg-amber-500',   text: 'text-amber-500',   bg: 'bg-amber-500/[0.08]',       border: 'border-amber-500/20',       icon: 'text-amber-500',       ring: 'ring-amber-500/30' },
  'yellow':      { dot: 'bg-yellow-500',  text: 'text-yellow-500',  bg: 'bg-yellow-500/[0.08]',      border: 'border-yellow-500/20',      icon: 'text-yellow-500',      ring: 'ring-yellow-500/30' },
  'violet':      { dot: 'bg-violet-500',  text: 'text-violet-400',  bg: 'bg-violet-500/[0.08]',      border: 'border-violet-500/20',      icon: 'text-violet-400',      ring: 'ring-violet-500/30' },
  'sky':         { dot: 'bg-sky-500',     text: 'text-sky-400',     bg: 'bg-sky-500/[0.08]',         border: 'border-sky-500/20',         icon: 'text-sky-400',         ring: 'ring-sky-500/30' },
}

function SuccessRing({ pct, active }: { pct: number; active: boolean }) {
  const r = 22
  const c = 2 * Math.PI * r
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * c
  return (
    <div className="relative shrink-0 w-14 h-14">
      <svg viewBox="0 0 60 60" className="w-full h-full -rotate-90">
        <circle cx="30" cy="30" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="4" />
        <circle
          cx="30" cy="30" r={r} fill="none"
          stroke={pct >= 80 ? 'hsl(142 71% 45%)' : pct >= 50 ? 'hsl(45 93% 55%)' : 'hsl(var(--destructive))'}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {pct > 0 ? (
          <span className="text-[11px] font-bold tabular-nums text-foreground leading-none">{pct}%</span>
        ) : (
          <Inbox className="w-4 h-4 text-muted-foreground/60" />
        )}
      </div>
      {active && (
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-background animate-pulse" />
      )}
    </div>
  )
}

function StatCard({ color, label, value, icon: Icon, sublabel }: {
  color: StatColor
  label: string
  value: number | string
  icon: React.ComponentType<{ className?: string }>
  sublabel?: string
}) {
  const c = STAT_COLORS[color]
  return (
    <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${c.border} ${c.bg} transition-all hover:ring-1 ${c.ring}`}>
      <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center bg-background/40 ${c.icon}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0 flex flex-col leading-tight">
        <span className={`text-base font-bold tabular-nums ${c.text}`}>{value}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          {label}
          {sublabel && <span className="ml-1 normal-case tracking-normal opacity-70">{sublabel}</span>}
        </span>
      </div>
    </div>
  )
}

function fmtDurationRu(sec: number): string {
  if (!sec || sec < 0) return '—'
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (d > 0) return `${d}д ${h}ч`
  if (h > 0) return `${h}ч ${m}м`
  if (m > 0) return `${m}м`
  return `${sec}с`
}

export default function ReportPanel({ stats, elapsedSec, running, campaignId, mode = 'booster' }: Props) {
  // Items теперь приходят напрямую из WS state (ok_grouped_by_cid с items).
  // Lazy-load через отдельный запрос убран — был ненадёжным.
  const isProfiler = mode === 'profiler'

  const okGrp: Record<string, any> = stats.okGrouped || {}
  const failGrp: Record<string, any> = stats.failGrouped || {}
  const tempGrp: Record<string, any> = stats.tempGrouped || {}

  // Profiler grouped «данные по прокачке» (group/party → mode → iterations).
  const profOkGrp: Record<string, any> = stats.profilerOkGrouped || {}
  const profFailGrp: Record<string, any> = stats.profilerFailGrouped || {}
  // Grouped sums are the per-mode/party breakdown, but they live in an
  // in-memory accumulator that resets on panel restart — so they under-count
  // the campaign lifetime (e.g. 329 vs 4214). The HEADLINE totals must match
  // the top result tiles, so prefer the authoritative persisted counters and
  // only fall back to grouped sums when they're unavailable.
  const profOkGrpTotal = sumGrouped(profOkGrp)
  const profFailGrpTotal = sumGrouped(profFailGrp)
  const profOkTotal = stats.profilerOkCount ?? profOkGrpTotal
  const profFailTotal = stats.profilerFailCount ?? profFailGrpTotal
  const profTotal = profOkTotal + profFailTotal
  // Rich profiler path when authoritative counters OR marker-fed grouped data
  // exist. Older engines (no PROFILER_RESULT marker / no counters) fall back to
  // the plain NonBooster list.
  const useProfRich = isProfiler && (profTotal > 0 || profOkGrpTotal > 0 || profFailGrpTotal > 0)

  const skipGrp: Record<string, any> = stats.skipGrouped || {}
  const boosterOkTotal = stats.boosterOkCount ?? sumGrouped(okGrp)
  const boosterFailTotal = stats.boosterFailCount ?? sumGrouped(failGrp)
  const boosterTempTotal = stats.boosterTempCount ?? sumGrouped(tempGrp)
  const boosterSkipTotal = stats.boosterSkipCount ?? sumGrouped(skipGrp)  // Idea #69
  const boosterCompleted = boosterOkTotal + boosterFailTotal
  const boosterTotal = boosterCompleted + boosterTempTotal
  // Never take the booster render branch for a profiler campaign.
  const isBooster = !isProfiler && boosterTotal > 0

  let total: number
  let ok: number
  let failTotal: number
  if (isBooster) {
    total = boosterCompleted
    ok = boosterOkTotal
    failTotal = boosterFailTotal
  } else if (useProfRich) {
    total = profTotal
    ok = profOkTotal
    failTotal = profFailTotal
  } else {
    total = stats.success + stats.fail + stats.recovered
    ok = stats.success
    failTotal = stats.fail
  }
  const speed = useSpeed(ok, stats.firstSuccessTs || (ok > 0 ? Date.now() - 1000 : 0))

  // okRows/failRows used to be derived from `stats.boosterOk`/`stats.boosterFail`
  // (live websocket Maps) — but the export now reads `okGrp`/`failGrp`
  // (persisted grouped state) directly, so the per-Map parse step is gone.

  // Success / fail percentages for the success-rate ring in the hero card.
  const successPct = total > 0 ? Math.round((ok / total) * 100) : 0

  return (
    <div className="h-full overflow-y-auto px-3 py-3 sm:px-4 select-text">
      {/* ── HERO HEADER ─────────────────────────────────────────────── */}
      <div className="mb-4 rounded-xl border border-border bg-card overflow-hidden">
        {/* Row 1: Big "Итого" + success ring + speed/elapsed/download */}
        <div className="flex items-stretch flex-wrap gap-3 p-3 sm:p-4">
          {/* Hero: total + success ring */}
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <SuccessRing pct={successPct} active={running ?? false} />
            <div className="flex flex-col leading-none">
              <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Итого</span>
              <span className="text-2xl sm:text-3xl font-bold tabular-nums text-foreground mt-0.5">{total.toLocaleString('ru-RU')}</span>
              <span className="text-[10px] text-muted-foreground mt-1">
                {ok > 0 && total > 0 && (
                  <>
                    <span className="text-emerald-500 font-semibold tabular-nums">{successPct}%</span>
                    <span className="ml-1">успешно</span>
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Right side: meta pills */}
          <div className="flex items-center gap-1.5 ml-auto flex-wrap">
            {elapsedSec !== undefined && elapsedSec > 0 && (
              <div className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border bg-muted/30">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="tabular-nums text-xs font-medium text-foreground">{fmtDurationRu(elapsedSec)}</span>
                {running && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
              </div>
            )}
            <div className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-chart-4/20 bg-chart-4/[0.08]" title={isBooster ? 'Переходов в час' : 'Успешных в час'}>
              <Zap className="w-3.5 h-3.5 text-chart-4" />
              <span className="tabular-nums text-xs font-bold text-chart-4">{speed}</span>
              <span className="text-[10px] text-muted-foreground">/ч</span>
            </div>
            {(isBooster || useProfRich) && total > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (campaignId) downloadFromServer(campaignId, useProfRich ? 'profiler' : 'booster')
                  else if (useProfRich) downloadProfilerTsv(profOkGrp, profFailGrp)
                  else downloadTsv(okGrp, failGrp)
                }}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-border bg-background hover:bg-muted/60 hover:border-foreground/20 transition-all cursor-pointer"
                title="Скачать отчёт (.tsv)"
              >
                <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Скачать</span>
              </button>
            )}
          </div>
        </div>

        {/* Row 2: stat cards grid (2 cols on mobile → up to 4 desktop) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 px-3 pb-3 sm:px-4 sm:pb-4">
          <StatCard color="emerald" label={useProfRich ? 'Прокачек' : 'Успех'} value={ok.toLocaleString('ru-RU')} icon={Trophy} />
          <StatCard color="destructive" label="Неудач" value={failTotal.toLocaleString('ru-RU')} icon={AlertCircle} />
          {isBooster && boosterTempTotal > 0 && (
            <StatCard color="amber" label="Врем." sublabel="трудности" value={boosterTempTotal.toLocaleString('ru-RU')} icon={Hourglass} />
          )}
          {!isBooster && !useProfRich && (
            <StatCard color="yellow" label="Восст." value={stats.recovered.toLocaleString('ru-RU')} icon={ShieldCheck} />
          )}
          {isBooster && boosterSkipTotal > 0 && (
            <StatCard color="sky" label="Пропуски" sublabel="SmartPilot" value={boosterSkipTotal.toLocaleString('ru-RU')} icon={SkipForward} />
          )}
          {stats.captchaTotal > 0 && (
            <StatCard color="violet" label="Капч" value={stats.captchaTotal.toLocaleString('ru-RU')} icon={Activity} />
          )}
        </div>
      </div>

      {isBooster ? (
        <>
          {boosterOkTotal > 0 && (
            <SectionBlock title="Успешные выполнения" count={boosterOkTotal} colorClass="text-success"
              defaultOpen totalAll={total} accent="emerald" icon={Trophy}>
              <OkGroupedRows grouped={okGrp} campaignId={campaignId} />
            </SectionBlock>
          )}
          {boosterFailTotal > 0 && (
            <SectionBlock title="Неудачные выполнения" count={boosterFailTotal} colorClass="text-destructive"
              totalAll={total} accent="destructive" icon={AlertCircle}>
              <GroupedRows grouped={stats.failGrouped || {}} />
            </SectionBlock>
          )}
          {boosterTempTotal > 0 && (
            <SectionBlock title="Временные трудности" count={boosterTempTotal} colorClass="text-amber-500"
              totalAll={total + boosterTempTotal} accent="amber" icon={Hourglass}>
              <GroupedRows grouped={stats.tempGrouped || {}} colorClass="text-amber-500" barClass="bg-amber-500/70" />
            </SectionBlock>
          )}
          {boosterSkipTotal > 0 && (
            <SectionBlock title="Пропущено (SmartPilot)" count={boosterSkipTotal} colorClass="text-sky-400"
              totalAll={total + boosterSkipTotal} accent="amber" icon={SkipForward}>
              <GroupedRows grouped={skipGrp} colorClass="text-sky-400" barClass="bg-sky-500/70" />
            </SectionBlock>
          )}
        </>
      ) : useProfRich ? (
        <>
          {profOkGrpTotal > 0 && (
            <SectionBlock title="Успешные прокачки" count={profOkGrpTotal} colorClass="text-success"
              defaultOpen totalAll={profOkGrpTotal + profFailGrpTotal} accent="emerald" icon={Trophy}>
              <ProfilerOkGroupedRows grouped={profOkGrp} campaignId={campaignId} />
            </SectionBlock>
          )}
          {profFailGrpTotal > 0 && (
            <SectionBlock title="Неудачные прокачки" count={profFailGrpTotal} colorClass="text-destructive"
              totalAll={profOkGrpTotal + profFailGrpTotal} accent="destructive" icon={AlertCircle}>
              <ProfilerFailGroupedRows grouped={profFailGrp} />
            </SectionBlock>
          )}
          {(profOkGrpTotal < profOkTotal || profFailGrpTotal < profFailTotal) && (
            <div className="mt-2 px-1 text-[11px] text-muted-foreground">
              Итоговые счётчики выше отражают всю кампанию. Детализация ниже — с момента
              последнего перезапуска панели.
            </div>
          )}
        </>
      ) : (
        <>
          <NonBoosterSection title="Успешные выполнения" count={stats.success} colorClass="text-success" entries={mapEntries(stats.successMessages)} />
          <NonBoosterSection title="Неудачные выполнения" count={stats.fail} colorClass="text-destructive" entries={mapEntries(stats.failMessages)} />
          <NonBoosterSection title="Неудачи и восстановлено" count={stats.recovered} colorClass="text-yellow-500" entries={mapEntries(stats.recoveredMessages)} />
        </>
      )}

      {total === 0 && (
        <EmptyState />
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-4">
      <div className="w-16 h-16 rounded-full bg-muted/40 flex items-center justify-center mb-3">
        <BarChart3 className="w-8 h-8 text-muted-foreground/50" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">Пока нет данных</p>
      <p className="text-xs text-muted-foreground max-w-[280px]">
        Здесь появятся успешные и неудачные выполнения, как только сценарий начнёт работать.
      </p>
    </div>
  )
}
